import {IssuePickerSelectedIssuesServerQuery} from '@github-ui/server-persisted-queries'
import type {IssuePickerSelectedIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssuePickerSelectedIssuesServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {IssuePickerIssueNode} from './issue-picker-selected-issues-query-types'

// The endpoint accepts at most 100 ids per request, so larger selections are split across requests.
export const SELECTED_ISSUES_BATCH_SIZE = 100

export function issuePickerSelectedIssuesQueryKey(owner: string, repo: string, ids: readonly string[]) {
  return ['item-picker', 'issue-picker-selected-issues', {owner, repo, ids}] as const
}

async function fetchBatch(owner: string, repo: string, ids: string[], signal?: AbortSignal) {
  const params = new URLSearchParams({queryId: IssuePickerSelectedIssuesServerQuery})
  for (const id of ids) {
    params.append('ids[]', id)
  }

  const response = await reactFetchJSON(`/${owner}/${repo}/issue_picker_selected_issues.json?${params.toString()}`, {
    signal,
  })
  if (!response.ok) throw new Error(`Selected issues fetch failed (${response.status})`)

  const json = (await response.json()) as {
    data?: IssuePickerSelectedIssuesServerQueryQuery
    errors?: unknown[]
  }
  if (!json.data) throw new Error('Selected issues query returned no data')

  return json.data.nodes
}

export async function fetchIssuePickerSelectedIssues(
  owner: string,
  repo: string,
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<IssuePickerIssueNode[]> {
  const batches: string[][] = []
  for (let index = 0; index < ids.length; index += SELECTED_ISSUES_BATCH_SIZE) {
    batches.push(ids.slice(index, index + SELECTED_ISSUES_BATCH_SIZE))
  }

  const responses = await Promise.all(batches.map(batch => fetchBatch(owner, repo, batch, signal)))

  return responses.flatMap(nodes => (nodes || []).flatMap(node => (node?.__typename === 'Issue' ? [node] : [])))
}
